/**
 * "What should be playing right now?" — one resolver, driven by the active view.
 *
 * Before this existed, each view called `playMusic()` on its own whenever it felt
 * like it. That produced exactly the two bugs the owner reported: the farm bed
 * kept playing over the shop / quests / awards / admin, and coming back from a
 * match left the arena bed running under the farm. Views no longer choose a
 * track; they declare where the player IS, and the answer is derived here.
 *
 * Kept free of DOM and of `audio.ts` so it can be unit-tested on its own.
 */

/** Music beds. Exactly one plays at a time. */
export type MusicTrack = 'day' | 'night' | 'arena' | 'victory';

/**
 * Where the player currently is.
 *
 * The tab ids from the shell (`farm`, `shop`, `arena`, `quests`, `awards`,
 * `admin`) are passed straight through, plus the two fullscreen overlays that
 * take over from a tab (`arena-match`, `dungeon`) and `none` for signed-out.
 *
 * `landing` is the marketing page, which is a view like any other so that its bed
 * is chosen HERE rather than by a second audio system living on the website side.
 */
export type AudioView =
  | 'farm' | 'shop' | 'arena' | 'quests' | 'awards' | 'admin'
  | 'arena-match' | 'arena-result' | 'dungeon' | 'landing' | 'none';

export interface AudioScene {
  /** The active tab. */
  view: AudioView;
  /**
   * A fullscreen overlay that has taken over from the tab (an arena match, a
   * dungeon). Wins over `view`, and clearing it hands ownership straight back —
   * which is how leaving a match reclaims the farm's day/night bed.
   */
  overlay?: AudioView | null;
  /** World clock phase, for the farm bed. Dusk and dawn count as night. */
  phase?: 'day' | 'night';
}

/**
 * The single mapping from a scene to its bed.
 *
 * Everything that is not the farm or a live match is DELIBERATELY silent: the
 * shop, quests, awards and admin are menus, and a farm bed droning under them
 * was the complaint. Returning `null` fades the current bed out and stops it.
 */
export function resolveTrack(scene: AudioScene): MusicTrack | null {
  const where = scene.overlay ?? scene.view;
  switch (where) {
    case 'arena-match': return 'arena';
    // The results overlay is its own "place" purely so the victory sting can own the
    // bed: switching to it cross-fades the dense battle bed OUT rather than playing
    // the sting on top of it. See VICTORY_STING_MS — the caller hands the bed back
    // once it has played, because playMusic() loops every bed and an 8-second
    // fanfare on repeat under three result screens is a punishment.
    case 'arena-result': return 'victory';
    // The dungeon is underground and permanently dark — the night bed fits it and
    // saves shipping a fifth music file.
    case 'dungeon': return 'night';
    case 'farm': return scene.phase === 'night' ? 'night' : 'day';
    // The landing page's hero art is the daytime farm, so it gets the day bed —
    // no fifth music file, and a visitor who presses play hears the game itself.
    // The landing view is only ever set by an explicit press on the speaker, so
    // returning a track here can never make a marketing page blare on load.
    case 'landing': return 'day';
    default: return null;
  }
}

/**
 * Per-bed gain trim, applied on top of the player's music volume.
 *
 * The arena sits at HALF the normal level (owner): the battle bed is dense and
 * was drowning out the combat it is supposed to underscore.
 */
export const TRACK_GAIN: Record<MusicTrack, number> = {
  day: 1,
  night: 1,
  arena: 0.5,
  victory: 0.8,
};

/**
 * Length of victory.m4a (8.0s, rounded down so the fade starts before the loop
 * point). The bed player has no one-shot mode, so whoever puts the sting on is
 * responsible for taking it off again after this long.
 */
export const VICTORY_STING_MS = 8_000;
